import { z } from 'zod';
import { DOC_KINDS } from './documents.js';

/** مخططات مستندات الموظّف. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

const optionalDateOnly = dateOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

/**
 * ورقةٌ تُودَع.
 *
 * و«أخرى» تلزمها تسمية: صفٌّ نوعُه «أخرى» بلا عنوانٍ لا يُعرف ما هو بعد
 * شهر، ويصير الملفُّ مجلَّدًا لا سجلًّا. والانتهاءُ بعد الإصدار — تاريخٌ
 * مقلوبٌ يجعل كلَّ ورقةٍ تبدو منتهية.
 */
export const UpsertDocumentSchema = z
  .object({
    kind: z.enum(DOC_KINDS),
    label: optionalText(160),
    number: optionalText(80),
    issuedOn: optionalDateOnly,
    expiresOn: optionalDateOnly,
    attachmentId: Cuid.nullable().optional(),
    notes: optionalText(1000),
  })
  .refine((v) => v.kind !== 'OTHER' || Boolean(v.label), {
    message: 'اكتب عنوانَ الورقة — «أخرى» وحدَها لا تُعرف بعد شهر.',
    path: ['label'],
  })
  .refine((v) => !v.issuedOn || !v.expiresOn || v.expiresOn >= v.issuedOn, {
    message: 'تاريخ الانتهاء قبل الإصدار.',
    path: ['expiresOn'],
  });
export type UpsertDocumentInput = z.infer<typeof UpsertDocumentSchema>;

export const DocumentsQuerySchema = z.object({
  employeeId: Cuid.optional(),
  kind: z.enum(DOC_KINDS).optional(),
  /** «ينتهي قريبًا أو انتهى» — ما يحتاج عملًا. */
  due: z.enum(['1']).optional(),
});
export type DocumentsQuery = z.infer<typeof DocumentsQuerySchema>;

/**
 * ما تطلبه الشركةُ من أوراق.
 *
 * إعدادُ شركةٍ لا ثابتٌ في الشيفرة: ما يلزم في مصرَ غيرُ ما يلزم في
 * الخليج، وفرضُ قائمةٍ واحدةٍ يجعل «ملفّاتٌ ناقصة» رقمًا لا يصف أحدًا.
 */
export const RequiredDocsSchema = z.object({
  kinds: z.array(z.enum(DOC_KINDS)).max(DOC_KINDS.length),
});
export type RequiredDocsInput = z.infer<typeof RequiredDocsSchema>;
